// VRP session management
import axios from 'axios';
import { 
  API_BASE_URL,
  VRP_SESSION_SAVE_AUTO_PATH,
  VRP_SESSION_SAVE_CUSTOM_PATH,
  VRP_SESSION_GET_PATHS,
  VRP_SESSION_CLEAR_ALL,
  VRP_SESSION_CLEAR_AUTO_PATH,
  VRP_SESSION_CLEAR_CUSTOM_PATH
} from '../../../../config';

// distance calc - copied from PathServices to avoid circular deps
const calculatePathDistance = (path, locations) => {
  if (!path || path.length < 2 || !locations || locations.length === 0) {
    return 0;
  }

  let totalDist = 0;  // shorter var name
  for (let i = 0; i < path.length - 1; i++) {
    const curr = path[i];
    const next = path[i + 1];
    
    const currLoc = locations.find(loc => loc.id === curr);
    const nextLoc = locations.find(loc => loc.id === next);
    
    if (currLoc && nextLoc) {
      const d = manhattanDistance(
        currLoc.latitude, 
        currLoc.longitude, 
        nextLoc.latitude, 
        nextLoc.longitude
      );
      totalDist += d;
    }
  }
  
  return totalDist;
};

// manhattan distance formula - copied from PathServices to avoid circular deps
const manhattanDistance = (lat1, lon1, lat2, lon2) => {
  // Convert degree differences to approximate kilometers
  const latDiff = Math.abs(lat2 - lat1);
  const lonDiff = Math.abs(lon2 - lon1);
  
  // Convert to kilometers using degree-to-km conversion
  const latKm = latDiff * 111.0;
  const lonKm = lonDiff * 111.0;
  
  // Manhattan distance in kilometers
  return latKm + lonKm;
};


export const saveAutoPathToSession = async (path, distance) => {
  const response = await axios.post(VRP_SESSION_SAVE_AUTO_PATH);
  
  if (!response.data.success) {
    console.warn('Failed to save auto path:', response.data.error);
    throw new Error(response.data.error || 'Could not save auto path');
  }
  
  return response.data.data;
};

export const saveCustomPathToSession = async (path, distance, metadata = null) => {
  try {
    const requestData = {
      path: path,
      distance: distance
    };
    
    // If metadata is an array, assume it's vehiclePaths
    if (Array.isArray(metadata)) {
      requestData.vehiclePaths = metadata;
    } 
    // If metadata is an object, add all its properties
    else if (metadata && typeof metadata === 'object') {
      // If vehiclePaths property exists, add to request
      if (metadata.vehiclePaths) {
        requestData.vehiclePaths = metadata.vehiclePaths;
      }
      
      // If customVehicleCount property exists, add to request
      if (metadata.customVehicleCount) {
        requestData.customVehicleCount = metadata.customVehicleCount;
      }
      
      // Add any other potential metadata properties
      Object.keys(metadata).forEach(key => {
        if (!['path', 'distance', 'vehiclePaths', 'customVehicleCount'].includes(key)) {
          requestData[key] = metadata[key];
        }
      });
    }
    
    
    const response = await axios.post(VRP_SESSION_SAVE_CUSTOM_PATH, requestData);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      console.error('Error returned from save custom path to session API:', response.data.error);
      throw new Error(response.data.error || 'Error saving custom path to session');
    }
  } catch (error) {
    console.error('Error calling save custom path to session API:', error);
    throw error;
  }
};

export const getSessionPaths = async () => {
  try {
    const response = await axios.get(VRP_SESSION_GET_PATHS);
    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.error('Could not load session paths:', error.message);
    return null; // Return null instead of throwing
  }
};

export const clearSessionPaths = async () => {
  try {
    await axios.post(VRP_SESSION_CLEAR_ALL);
    return true;
  } catch (err) {
    console.warn('Session clear failed, but continuing anyway');
    return false;
  }
};

export const clearSessionAutoPath = async () => {
  try {
    const response = await axios.post(VRP_SESSION_CLEAR_AUTO_PATH);
    
    if (response.data.success) {
      return true;
    } else {
      console.error('Error returned from clear session auto path API:', response.data.error);
      throw new Error(response.data.error || 'Error clearing session auto path');
    }
  } catch (error) {
    console.error('Error calling clear session auto path API:', error);
    throw error;
  }
};

export const clearSessionCustomPath = async () => {
  try {
    const response = await axios.post(VRP_SESSION_CLEAR_CUSTOM_PATH);
    
    if (response.data.success) {
      return true;
    } else {
      console.error('Error returned from clear session custom path API:', response.data.error);
      throw new Error(response.data.error || 'Error clearing session custom path');
    }
  } catch (error) {
    console.error('Error calling clear session custom path API:', error);
    throw error;
  }
};

export const deleteGroup = async (groupId) => {
  const response = await axios.delete(`${API_BASE_URL}/api/storage/paths/group/${groupId}`);
  
  if (response.data?.success) {
    return response.data.data;
  }
  
  // Just log and continue if deletion fails
  console.log(`Group ${groupId} deletion had issues, but that's probably fine`);
  return null;
};

export const cleanupVrpSession = async () => {
  try {
    await clearSessionPaths();  // just clear everything
  } catch (error) {
    console.error('Cleanup failed:', error);
  }
};

export const saveOptimizedAutoPath = async (path, locations, prevPath = null) => {
  try {
    const pathIds = Array.isArray(path) ? path : [];
    const metadata = path?.vehiclePaths ? { vehiclePaths: path.vehiclePaths } : null;
    
    const distance = calculatePathDistance(pathIds, locations);
    
    // skip if same as previous
    if (prevPath && Array.isArray(prevPath) && 
        prevPath.length === pathIds.length && 
        prevPath.every((id, idx) => id === pathIds[idx])) {
      return true;  // no need to save again
    }
    
    // Save to session
    await saveAutoPathToSession(pathIds, distance);
    
    // If there is metadata, save it separately
    if (metadata) {
      await saveCustomPathToSession(pathIds, distance, metadata.vehiclePaths);
    }
    
    return true;
  } catch (error) {
    console.error('Error saving optimized auto path:', error);
    throw error;
  }
};

export const saveOptimizedCustomPath = async (path, locations, prevPath = null) => {
  try {
    // Extract path ID array and metadata
    const pathIds = Array.isArray(path) ? path : [];
    const metadata = {};
    
    // Extract vehiclePaths
    if (path && path.vehiclePaths) {
      metadata.vehiclePaths = path.vehiclePaths;
    }
    
    // Extract customVehicleCount
    if (path && path.customVehicleCount) {
      metadata.customVehicleCount = path.customVehicleCount;

    } else if (metadata.vehiclePaths && metadata.vehiclePaths.length > 0) {
      metadata.customVehicleCount = metadata.vehiclePaths.length;

    }
    
    // Calculate path distance
    const distance = calculatePathDistance(pathIds, locations);
    
    // Check if path is same as previous path
    if (prevPath && 
        Array.isArray(prevPath) && 
        prevPath.length === pathIds.length && 
        prevPath.every((id, index) => id === pathIds[index])) {

      return true;
    }
    
    // Print data to be saved

    // Build save request data
    const saveData = {
      path: pathIds,
      distance: distance
    };
    
    // Add metadata
    if (metadata.vehiclePaths) {
      saveData.vehiclePaths = metadata.vehiclePaths;
    }
    
    if (metadata.customVehicleCount) {
      saveData.customVehicleCount = metadata.customVehicleCount;
    }
    
    // Save to session
    await saveCustomPathToSession(saveData.path, saveData.distance, saveData);
    
    return true;
  } catch (error) {
    console.error('Error saving optimized custom path:', error);
    throw error;
  }
};

export const saveUserPath = async (groupId, path, distance, metadata = {}) => {
  try {
    const requestData = {
      group_id: groupId,
      module: "vrp",
      path: path,
      distance: distance
    };

    // If metadata is an array, assume it's vehiclePaths
    if (Array.isArray(metadata)) {
      requestData.vehicle_paths = metadata;
    }
    // If metadata is an object, add all its properties
    else if (metadata && typeof metadata === 'object') {
      // If vehiclePaths property exists, add to request
      if (metadata.vehiclePaths) {
        requestData.vehicle_paths = metadata.vehiclePaths;
      }
      
      // If customVehicleCount property exists, add to request
      if (metadata.customVehicleCount) {
        requestData.custom_vehicle_count = metadata.customVehicleCount;
      }
      
      // Add any other potential metadata properties
      Object.entries(metadata).forEach(([key, value]) => {
        if (!['vehiclePaths', 'customVehicleCount'].includes(key)) {
          requestData[key] = value;
        }
      });
    }
    
    // console.log('[DEBUG-SESSION] Saving custom path with request data:', requestData);
    
    const response = await axios.post(VRP_SESSION_SAVE_CUSTOM_PATH, requestData);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      console.error('Error returned from save custom path to session API:', response.data.error);
      throw new Error(response.data.error || 'Error saving custom path to session');
    }
  } catch (error) {
    console.error('Error calling save custom path to session API:', error);
    throw error;
  }
}; 